import { useState, useEffect, useRef, useMemo } from 'preact/hooks'
import { Users, Plus, Trash2, ChevronDown, Building2, Check, Mail, Edit } from 'lucide-preact'
import { useAuth } from '../../../auth/useAuth'
import { useUserProfile, USER_ROLES } from '../../../hooks/useUserProfile'
import { useToast } from '@/hooks/useToast'
import { userRolesApi } from '../../../api/userRoles'
import { UserRoleEditModal } from '../../modals/UserRoleEditModal'
import { UserCreateModal } from '../../modals/UserCreateModal'
import { ConfirmationModal } from '../../modals/ConfirmationModal'
import { PersonAvatar } from '../../common/Avatar'
import { SegmentedToggle } from '@/components/common/SegmentedToggle'
import { DataGrid, type ColumnConfig } from '@/components/common/grids/DataGrid'
import { RoleBadge } from './RoleBadge'
import { InviteAccessBadge } from './InviteStatusBadge'
import { STUCK_ACCESS_STATES, canReInvite, accessUrgency } from './inviteStatus'
import { UserDetailFlyout } from './UserDetailFlyout'
import type { ClientCompanyViewDto, ClientWorkspaceViewDto } from '@/types/company'
import type {
  UserProfileWithRolesDto,
  RoleType,
  WorkspaceRolesViewDto,
  UserInviteStatusDto,
  CompanyInviteStatusDto,
} from '@/types/userRoles'

interface UserManagementProps {
  selectedCompany: ClientCompanyViewDto
}

type Tab = 'members' | 'attention'

/** Addresses are stored verbatim on both sides and never normalised, so the join has to fold case. */
const emailKey = (email: string) => email.trim().toLowerCase()

function UserManagementWorkspaceSelector({
  selectedWorkspaceId,
  onWorkspaceChange,
  manageableWorkspaces,
  isCompanyAdmin
}: {
  selectedWorkspaceId: string
  onWorkspaceChange: (workspaceId: string) => void
  manageableWorkspaces: ClientWorkspaceViewDto[]
  isCompanyAdmin: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setIsOpen(false)
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const selectedWorkspace = selectedWorkspaceId === 'all'
    ? { Name: 'All Workspaces' }
    : manageableWorkspaces.find(ws => ws.Id === selectedWorkspaceId)

  const select = (workspaceId: string) => { onWorkspaceChange(workspaceId); setIsOpen(false) }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
      >
        <Building2 className="w-4 h-4" />
        <span>{selectedWorkspace?.Name || 'Select Workspace'}</span>
        <ChevronDown className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg z-10">
          {isCompanyAdmin && (
            <button onClick={() => select('all')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
              <div className="w-4 h-4 flex items-center justify-center">{selectedWorkspaceId === 'all' && <Check className="w-3 h-3" />}</div>
              <Building2 className="w-4 h-4" />
              <span>All Workspaces</span>
            </button>
          )}
          {manageableWorkspaces.map(workspace => (
            <button key={workspace.Id} onClick={() => select(workspace.Id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
              <div className="w-4 h-4 flex items-center justify-center">{selectedWorkspaceId === workspace.Id && <Check className="w-3 h-3" />}</div>
              <Building2 className="w-4 h-4" />
              <span>{workspace.Name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function UserManagement({ selectedCompany }: UserManagementProps) {
  const { user, userProfile, isParentCompany } = useAuth()
  const { getCurrentCompanyRoleSet, hasCompanyRole } = useUserProfile()
  const { showSuccess, showWarning, showError } = useToast()

  const [userProfiles, setUserProfiles] = useState<UserProfileWithRolesDto[]>([])
  const [inviteStatus, setInviteStatus] = useState<Map<string, UserInviteStatusDto> | null>(null)
  const [statusHealth, setStatusHealth] = useState<CompanyInviteStatusDto | null>(null)
  /** The status call came back and we got nothing usable from it. Distinct from it still being out. */
  const [statusLoadFailed, setStatusLoadFailed] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('all')
  const [tab, setTab] = useState<Tab>('members')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editingUser, setEditingUser] = useState<UserProfileWithRolesDto | null>(null)
  const [isRoleEditModalOpen, setIsRoleEditModalOpen] = useState(false)
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false)
  const [detailUser, setDetailUser] = useState<UserProfileWithRolesDto | null>(null)
  const [reInvitingId, setReInvitingId] = useState<string | null>(null)

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [userToDelete, setUserToDelete] = useState<{
    id: string
    name: string
    companyId: string
    companyName: string
    isHomeCompany: boolean
  } | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const userRoles = getCurrentCompanyRoleSet()
  const isCompanyAdmin = userRoles?.IsAdmin || hasCompanyRole(USER_ROLES.ADMIN, selectedCompany.Id)

  // The company the screen is currently showing. A ref rather than the prop, because both loads close
  // over the selectedCompany of the render that started them — comparing that prop against itself always
  // matches, so it would catch nothing.
  //
  // Assigned during render rather than in the effect below. Nothing can call a load in the gap between
  // the two, so the effect was not actually wrong, but keeping it here means the guard does not depend
  // on that argument holding for every future caller.
  const activeCompanyIdRef = useRef(selectedCompany.Id)
  activeCompanyIdRef.current = selectedCompany.Id

  useEffect(() => { loadUserRoles() }, [selectedCompany.Id])

  const loadUserRoles = async () => {
    if (!user) {
      setLoading(false)
      return
    }
    const companyId = selectedCompany.Id
    setLoading(true)
    setError(null)
    setInviteStatus(null)
    setStatusHealth(null)
    setStatusLoadFailed(false)

    const response = await userRolesApi.getUserRolesByCompany(companyId)
    // Switching company mid-flight makes this answer about the company we left. Dropping it is the whole
    // fix: the switch started its own load, and letting the older one land would list one company's
    // people under another company's name. Loading is left alone because that newer load owns it now.
    if (activeCompanyIdRef.current !== companyId) return

    if (response.error) {
      setError(response.error)
      setLoading(false)
      return
    }

    setUserProfiles(response.data || [])
    setLoading(false)
    loadInviteStatus(companyId)
  }

  // Second call, after the list is on screen. The list must never wait on Auth0, and must never be taken
  // down by it: an Auth0 incident would otherwise remove the ability to manage users, including the
  // ability to re-invite.
  //
  // Takes the company explicitly so the follow-on call inherits the one its list was read for, rather
  // than whichever is selected by the time it runs.
  const loadInviteStatus = async (companyId: string = selectedCompany.Id) => {
    const response = await userRolesApi.getInviteStatus(companyId)
    if (activeCompanyIdRef.current !== companyId) return
    if (response.error || !response.data) {
      // Recorded rather than swallowed. The call failing and the call still being out both leave
      // inviteStatus null, and without this the screen cannot tell them apart — so it would go on
      // saying it was checking long after it had stopped.
      setStatusLoadFailed(true)
      return
    }

    setStatusHealth(response.data)
    setInviteStatus(new Map(response.data.Items.map(item => [emailKey(item.Email), item])))
  }

  const statusFor = (profile: UserProfileWithRolesDto) => inviteStatus?.get(emailKey(profile.Email))

  // ORB-1019: access written by the managing company's "Access to all customers" switch. Only the switch
  // changes it, and the API refuses Edit and Remove too; the row says who manages it instead.
  const isManagedBySwitch = (profile: UserProfileWithRolesDto) => profile.UserCompanyRoleSet?.Source === 'AllCustomers'
  // Their home company IS the managing company, which is what the status call names.
  const managedByLabel = (profile: UserProfileWithRolesDto) =>
    `Access managed by ${statusFor(profile)?.HomeCompanyName ?? 'the managing company'}`
  const managedById = (profile: UserProfileWithRolesDto) => `managed-by-${profile.UserProfileId}`

  const getManageableWorkspaces = (): ClientWorkspaceViewDto[] => {
    if (!userRoles || !selectedCompany.Workspaces) return []
    if (userRoles.IsAdmin || hasCompanyRole(USER_ROLES.ADMIN, selectedCompany.Id)) return selectedCompany.Workspaces

    const adminWorkspaceIds = userRoles.WorkspaceRoles
      .filter((wsRole: WorkspaceRolesViewDto) => wsRole.CanAdmin)
      .map((wsRole: WorkspaceRolesViewDto) => wsRole.WorkspaceId)

    return selectedCompany.Workspaces.filter(ws => adminWorkspaceIds.includes(ws.Id))
  }
  const manageableWorkspaces = getManageableWorkspaces()

  const searched = useMemo(() => {
    let filtered = userProfiles

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(profile => {
        const displayName = `${profile.FirstName} ${profile.LastName}`.toLowerCase()
        const roles = profile.UserCompanyRoleSet
          ? [...profile.UserCompanyRoleSet.CompanyRoles, ...profile.UserCompanyRoleSet.WorkspaceRoles.flatMap(ws => ws.Roles)].join(' ').toLowerCase()
          : ''
        return displayName.includes(query) || profile.Email.toLowerCase().includes(query) || roles.includes(query)
      })
    }

    if (selectedWorkspaceId !== 'all') {
      filtered = filtered.filter(profile => {
        // Somebody with no roles at all still belongs on this screen — they are usually the person who
        // cannot get in, which is exactly who an admin came here to find.
        if (!profile.UserCompanyRoleSet) return true
        return profile.UserCompanyRoleSet.CompanyRoles.length > 0 ||
          profile.UserCompanyRoleSet.WorkspaceRoles.some(wsRole => wsRole.WorkspaceId === selectedWorkspaceId)
      })
    }

    return filtered
  }, [userProfiles, searchQuery, selectedWorkspaceId])

  const needsAttention = (profile: UserProfileWithRolesDto) => {
    const status = statusFor(profile)
    return status ? STUCK_ACCESS_STATES.includes(status.AccessState) : false
  }

  const attentionRows = useMemo(() => searched.filter(needsAttention), [searched, inviteStatus])
  const memberRows = useMemo(() => searched.filter(p => !needsAttention(p)), [searched, inviteStatus])
  const rows = tab === 'attention' ? attentionRows : memberRows

  // The tab badges count the whole workspace, not the search. The search box moved inside the grid card
  // (ORB-715), which put it BELOW the badges it narrows — so a search that matched nobody would drop
  // "Needs attention" to 0 and read as "nobody in this company is stuck", which is the opposite of what
  // this screen exists to say. The badges stay a statement about the company; only the grid narrows.
  const unsearched = useMemo(
    () => (selectedWorkspaceId === 'all'
      ? userProfiles
      : userProfiles.filter(p => !p.UserCompanyRoleSet
        || p.UserCompanyRoleSet.CompanyRoles.length > 0
        || p.UserCompanyRoleSet.WorkspaceRoles.some(ws => ws.WorkspaceId === selectedWorkspaceId))),
    [userProfiles, selectedWorkspaceId],
  )
  const attentionTotal = useMemo(() => unsearched.filter(needsAttention).length, [unsearched, inviteStatus])
  const memberTotal = unsearched.length - attentionTotal

  // This tab is empty in three different situations and only one of them means nobody is stuck. Saying
  // "everyone can get in" while the status is unknown tells an admin the opposite of the truth about the
  // exact population this screen exists to surface.
  //
  // statusLoadFailed is read only where there is no payload to contradict it. Scoping it that way rather
  // than clearing it on every success is what keeps it from going stale: loadInviteStatus is also called
  // after Add User and after a re-invite, and a flag that has to be cleared in each of those places is a
  // flag that will eventually not be.
  const attentionEmptyMessage = () => {
    const unreadable = 'Sign-in status could not be read, so nobody can be listed here yet.'
    if (!inviteStatus) return statusLoadFailed ? unreadable : 'Checking who can get in…'
    // A payload that arrived while reporting Auth0 unreadable is the same unknown by another route.
    if (statusHealth && !statusHealth.Auth0Available) return unreadable
    return 'Everyone can get in. Nobody is waiting on an invitation.'
  }

  const handleReInvite = async (profile: UserProfileWithRolesDto) => {
    setReInvitingId(profile.UserProfileId)
    const response = await userRolesApi.reInviteUser(profile.UserProfileId, selectedCompany.Id)
    setReInvitingId(null)

    if (response.error || !response.data) {
      showError('Could not re-invite', response.error || 'The invitation could not be sent.')
      return
    }

    const result = response.data
    if (result.Outcome === 'Cooldown') {
      const minutes = Math.ceil((result.CooldownSecondsRemaining ?? 0) / 60)
      showWarning('Already sent recently', `An invitation went out moments ago. Re-inviting replaces that link, so wait about ${minutes} minute${minutes === 1 ? '' : 's'} before sending another.`)
      return
    }
    if (result.Outcome === 'NoInviteRequired') {
      showWarning('No invitation needed', result.Reason || 'This person reaches the company through their roles rather than an invitation.')
      return
    }
    if (result.InviteEmail && !result.InviteEmail.Sent) {
      showWarning('Invitation email not sent', result.InviteEmail.Reason || 'A fresh link was created but the email did not go out.')
    } else {
      showSuccess('Invitation sent', 'A fresh invitation is on its way. The previous link no longer works.')
    }

    loadInviteStatus()
  }

  const handleDeleteUser = (userId: string) => {
    const profile = userProfiles.find(u => u.UserProfileId === userId)
    if (!profile) return
    setUserToDelete({
      id: userId,
      name: `${profile.FirstName} ${profile.LastName}`.trim() || profile.Email,
      // Fixed at the click, so the dialog and the removal both name the company the admin was looking at,
      // even if the screen switches company while the dialog is open.
      companyId: selectedCompany.Id,
      companyName: selectedCompany.Name,
      // ClientCompanyId is the person's home company. Removing them there takes every company they belong
      // to and their sign-in with it; removing a visitor takes only this company (ORB-1075).
      isHomeCompany: profile.ClientCompanyId === selectedCompany.Id,
    })
    setShowDeleteConfirm(true)
  }

  const confirmDeleteUser = async () => {
    if (!userToDelete) return
    setDeleteLoading(true)
    setError(null)

    const response = await userRolesApi.deleteUser(userToDelete.id, userToDelete.companyId)
    setDeleteLoading(false)

    if (response.error) {
      setError(response.error)
      return
    }

    // A screen that has since moved to another company shows a list the removal did not touch.
    if (activeCompanyIdRef.current === userToDelete.companyId) {
      setUserProfiles(prev => prev.filter(u => u.UserProfileId !== userToDelete.id))
    }
    setShowDeleteConfirm(false)
    setUserToDelete(null)
  }

  const handleRoleEditSuccess = (updatedUser: UserProfileWithRolesDto) => {
    setUserProfiles(prev => prev.map(p => p.UserProfileId === updatedUser.UserProfileId ? updatedUser : p))
  }

  const handleCreateUserSuccess = (newUser: UserProfileWithRolesDto) => {
    setIsCreateUserModalOpen(false)
    setUserProfiles(prev => [...prev, newUser])
    loadInviteStatus()
  }

  // ORB-1019: the picker stays open after Add, so each row can report its result; the list updates behind it.
  const handleUsersAdded = (newUsers: UserProfileWithRolesDto[]) => {
    const addedIds = new Set(newUsers.map(newUser => newUser.UserProfileId))
    setUserProfiles(prev => [...prev.filter(p => !addedIds.has(p.UserProfileId)), ...newUsers])
    loadInviteStatus()
  }

  const columns: ColumnConfig<UserProfileWithRolesDto>[] = [
    {
      key: 'name',
      header: 'User',
      sortable: true,
      accessor: (u) => `${u.FirstName} ${u.LastName}`.trim() || u.Email,
      render: (u) => (
        <div className="flex items-center gap-3 min-w-0">
          <PersonAvatar firstName={u.FirstName} lastName={u.LastName} profilePicUrl={u.AvatarUrl} size="sm" />
          <div className="min-w-0">
            <div className="font-medium text-slate-900 dark:text-white truncate">
              {`${u.FirstName} ${u.LastName}`.trim() || u.Email}
            </div>
            <div className="text-slate-500 dark:text-slate-400 truncate">{u.Email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      accessor: (u) => statusFor(u)?.AccessState ?? '',
      render: (u) => {
        const status = statusFor(u)
        const badge = status
          ? <InviteAccessBadge state={status.AccessState} />
          : <span className="inline-block h-5 w-24 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse" aria-label="Loading status" />
        if (!isManagedBySwitch(u)) return badge
        return (
          <div className="flex flex-col items-start gap-0.5">
            {badge}
            <small id={managedById(u)} className="text-xs text-slate-500 dark:text-slate-400">{managedByLabel(u)}</small>
          </div>
        )
      },
    },
    {
      key: 'roles',
      header: 'Roles',
      hidden: 'hidden md:table-cell',
      render: (u) => {
        const roleSet = u.UserCompanyRoleSet
        const roles: RoleType[] = roleSet
          ? (roleSet.CompanyRoles.length > 0
            ? roleSet.CompanyRoles
            : Array.from(new Set(roleSet.WorkspaceRoles.flatMap(ws => ws.Roles))))
          : []

        if (roles.length === 0) {
          return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>No roles</span>
        }

        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            {roles.slice(0, 3).map(role => <RoleBadge key={role} role={role} />)}
            {roles.length > 3 && (
              <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">+{roles.length - 3}</span>
            )}
          </div>
        )
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '132px',
      render: (u) => {
        const status = statusFor(u)
        const showReInvite = status ? canReInvite(status.AccessState, status.Delivery?.State) : false

        return (
          <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
            {showReInvite && (
              <button
                onClick={() => handleReInvite(u)}
                disabled={reInvitingId === u.UserProfileId}
                className="p-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors duration-200 disabled:opacity-50"
                title="Re-invite"
                aria-label={`Re-invite ${u.Email}`}
              >
                <Mail className="w-4 h-4" />
              </button>
            )}
            {isManagedBySwitch(u) ? (
              // Visible but disabled, so the row still says what it would offer. A disabled button shows
              // no tooltip in every browser, so the reason sits on a wrapper as well as in the status.
              <>
                <span title={managedByLabel(u)}>
                  <button
                    disabled
                    className="p-1.5 text-slate-300 dark:text-slate-600 rounded cursor-not-allowed"
                    aria-label={`Edit roles for ${u.Email}`}
                    aria-describedby={managedById(u)}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </span>
                {isCompanyAdmin && (
                  <span title={managedByLabel(u)}>
                    <button
                      disabled
                      className="p-1.5 text-slate-300 dark:text-slate-600 rounded cursor-not-allowed"
                      aria-label={`Remove ${u.Email}`}
                      aria-describedby={managedById(u)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </span>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => { setEditingUser(u); setIsRoleEditModalOpen(true) }}
                  className="p-1.5 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors duration-200"
                  title="Edit roles"
                  aria-label={`Edit roles for ${u.Email}`}
                >
                  <Edit className="w-4 h-4" />
                </button>
                {isCompanyAdmin && (
                  <button
                    onClick={() => handleDeleteUser(u.UserProfileId)}
                    className="p-1.5 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors duration-200"
                    title="Remove user"
                    aria-label={`Remove ${u.Email}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>
        )
      },
    },
  ]

  const countBadge = (total: number | null) =>
    total === null ? undefined : (
      <span className="ml-1.5 text-xs tabular-nums text-slate-500 dark:text-slate-400">{total}</span>
    )

  return (
    <div className="space-y-4">
      {/* Both children are conditional, and the search that used to hold this row up unconditionally is
          in the grid's strip now — so for a non-admin with no manageable workspaces it would render as
          an empty 16px band. */}
      {(manageableWorkspaces.length > 0 || isCompanyAdmin) && (
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {manageableWorkspaces.length > 0 && (
            <UserManagementWorkspaceSelector
              selectedWorkspaceId={selectedWorkspaceId}
              onWorkspaceChange={setSelectedWorkspaceId}
              manageableWorkspaces={manageableWorkspaces}
              isCompanyAdmin={isCompanyAdmin}
            />
          )}
        </div>

        {/* The search moved into the grid's header strip (ORB-715); Add User stayed. It acts on the
            company rather than on the list, and a px-4 py-2 primary CTA would tower over a strip of
            text-xs controls. */}
        {isCompanyAdmin && (
          <button
            onClick={() => setIsCreateUserModalOpen(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        )}
      </div>
      )}

      <SegmentedToggle<Tab>
        ariaLabel="User view"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'members', label: 'Members', icon: <Users className="w-4 h-4" />, badge: countBadge(inviteStatus ? memberTotal : null) },
          { value: 'attention', label: 'Needs attention', icon: <Mail className="w-4 h-4" />, badge: countBadge(inviteStatus ? attentionTotal : null) },
        ]}
      />

      {statusHealth && !statusHealth.Auth0Available && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
          {statusHealth.Auth0ScopeMissing ? (
            <>
              <span className="font-medium">Sign-in status cannot be read for this environment.</span>{' '}
              This looks like a configuration problem rather than an outage: the Auth0 application is
              missing a permission it needs. Everyone below shows an unknown status until it is granted.
            </>
          ) : (
            <>
              <span className="font-medium">Sign-in status is temporarily unavailable.</span>{' '}
              Auth0 could not be reached, so the status below is unknown rather than a statement about
              anyone's access. Roles and everything else on this page are unaffected.
            </>
          )}
        </div>
      )}

      {statusHealth && statusHealth.Auth0Available && !statusHealth.EmailDeliveryAvailable && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
          Invitation email records could not be read, so no delivery detail is shown. Sign-in status
          below is unaffected.
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center justify-between gap-4">
          <span><span className="font-medium">Could not load users.</span> {error}</span>
          {/* A transient failure has to be recoverable in place. Handing the error to DataGrid renders a
              state with no way out, so the only recovery is reloading the page. */}
          <button
            type="button"
            onClick={loadUserRoles}
            className="flex-shrink-0 rounded-lg bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-sm font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      <DataGrid
        title="Users"
        data={rows}
        columns={columns}
        getRowKey={(u) => u.UserProfileId}
        isLoading={loading}
        pagination
        pageSize={10}
        pageResetKey={`${tab}|${searchQuery}|${selectedWorkspaceId}`}
        defaultSortKey="name"
        // Status only. It sorts by urgency rank rather than by the state's name — "Invite expired" has
        // to come above "Active", and no raw value says that. Every other key returns undefined and
        // falls through to its column's accessor; answering for all of them would shadow those
        // accessors and quietly sort a future column by name instead of by itself.
        getSortComparator={(key) =>
          key === 'status'
            ? (a, b) => accessUrgency(statusFor(a)?.AccessState) - accessUrgency(statusFor(b)?.AccessState)
            : undefined
        }
        onRowClick={(u) => setDetailUser(u)}
        emptyMessage={
          tab === 'attention'
            ? attentionEmptyMessage()
            : searchQuery ? 'No users match your search.' : `No users have been assigned roles in ${selectedCompany.Name} yet.`
        }
        search={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search users',
          ariaLabel: 'Search users, emails or roles',
        }}
      />

      {detailUser && (
        <UserDetailFlyout
          userProfile={detailUser}
          status={statusFor(detailUser)}
          workspaces={selectedCompany.Workspaces}
          reInviting={reInvitingId === detailUser.UserProfileId}
          onReInvite={handleReInvite}
          onClose={() => setDetailUser(null)}
        />
      )}

      {editingUser && (
        <UserRoleEditModal
          isOpen={isRoleEditModalOpen}
          user={editingUser}
          companyWorkspaces={selectedCompany.Workspaces || []}
          allUsers={userProfiles}
          onClose={() => { setEditingUser(null); setIsRoleEditModalOpen(false) }}
          onSuccess={handleRoleEditSuccess}
          // ORB-1019: only on the managing company's own page, for its admins, about its own people.
          // The endpoint refuses anyone else, so this only keeps the switch off screens it cannot work on.
          allCustomersAccess={isParentCompany && isCompanyAdmin && !selectedCompany.ManagedByCompanyId
            && selectedCompany.Id === userProfile?.ClientCompanyId
            && editingUser.ClientCompanyId === selectedCompany.Id
            ? { managingCompanyId: selectedCompany.Id, managingCompanyName: selectedCompany.Name }
            : undefined}
        />
      )}

      <UserCreateModal
        isOpen={isCreateUserModalOpen}
        selectedCompany={selectedCompany}
        onClose={() => setIsCreateUserModalOpen(false)}
        onSuccess={handleCreateUserSuccess}
        onUsersAdded={handleUsersAdded}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Remove User"
        message={userToDelete?.isHomeCompany
          ? `Remove "${userToDelete.name}"? They'll lose access to ${userToDelete.companyName} and every other company they belong to, and won't be able to sign in. Everything they've worked on stays.`
          : `Remove "${userToDelete?.name}" from ${userToDelete?.companyName}? Only their access here is removed.`}
        type="warning"
        confirmText="Remove User"
        cancelText="Cancel"
        onConfirm={confirmDeleteUser}
        onCancel={() => { setShowDeleteConfirm(false); setUserToDelete(null) }}
        loading={deleteLoading}
      />
    </div>
  )
}
